import React, { useState } from "react";
import { getSidebarMenuConfig } from "./config/menu.config";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarLogo from "./SidebarLogo";
import SidebarMenuItem from "./components/SidebarMenuItem";
import "./Sidebar.css";
import { openItemOnSideBarClick } from "./utils/openItemOnSideBarClick";
import { AI_CANDIDATES_MENU_KEY } from "./config/menu.keys";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { useGetJobSkills } from "@services/jobSkills.service";
import { SIDEBAR_EXPANDED_WIDTH_PX } from "@config/theme/tokens";
import FilterModal from "@components/common/Table/Filter/FilterModal/FilterModal";

// Menu item type
interface MenuItem {
	key: string;
	label: string;
	icon: React.ReactNode;
	route: string;
	children?: MenuItem[];
}

const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const menu = getSidebarMenuConfig(t);
	const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
	
	// AI Candidates Filter Modal state
	const [showAIFilterModal, setShowAIFilterModal] = useState(false);
	const { 
		jobSkills, 
		hasNextPage: hasMoreSkills, 
		fetchNextPage: fetchMoreSkills, 
		isFetchingNextPage: isLoadingMoreSkills 
	} = useGetJobSkills(showAIFilterModal, true);

	const toggleMenu = (menuKey: string) => {
		setExpandedMenus(prev => (prev.includes(menuKey) ? prev.filter(key => key !== menuKey) : [...prev, menuKey]));
	};

	const isMenuExpanded = (menuKey: string) => expandedMenus.includes(menuKey);
	const isActiveRoute = (route: string) => {
		// Special handling for Course Builder routes
		if (route === "/course-builder") {
			return location.pathname.startsWith("/course-builder");
		}
		return location.pathname === route;
	};
	const isChildActive = (children: MenuItem[]) => {
		return children?.some(child => isActiveRoute(child.route));
	};

	// Handle AI Candidates click - show filter modal instead of navigating
	const handleAICandidatesClick = () => {
		setShowAIFilterModal(true);
	};

	// Handle AI Filter Modal Apply - navigate with filters
	const handleAIFilterApply = React.useCallback((filters: Record<string, unknown>) => {
		const skills = filters.skills as string[];
		if (skills && skills.length > 0) {
			// Navigate with filters in location state
			navigate(BROWSER_ROUTES.AI_CANDIDATES, {
				state: { filters: { skills } },
			});
		}
		setShowAIFilterModal(false);
	}, [navigate]);

	// Convert job skills to filter options format
	const skillOptions = jobSkills?.map(skill => ({
		value: skill.uuid || skill.id || skill.value || "",
		label: skill.name || skill.label || skill.value || "",
	})) || [];

	// AI Filter Modal Config
	const aiFilterModalConfig = React.useMemo(() => ({
		title: "Select Skills for AI Matching",
		sections: [
			{
				title: "",
				fields: [
					{
						name: "skills",
						label: "Skills",
						type: "multiselect" as const,
						placeholder: "Select Skills",
						options: skillOptions,
						value: [],
						hasNextPage: hasMoreSkills,
						onLoadMore: () => fetchMoreSkills(),
						isLoadingMore: isLoadingMoreSkills,
					},
				],
			},
		],
		onApply: handleAIFilterApply,
		onReset: () => {
			setShowAIFilterModal(false);
		},
	}), [skillOptions, handleAIFilterApply, hasMoreSkills, fetchMoreSkills, isLoadingMoreSkills]);

	// Recursive render for menu items
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const renderMenuItems = (items: MenuItem[], _parentKey: string = "", level: number = 0) =>
		items.map(item => {
	const hasChildren = Boolean(item.children?.length);
	const isExpanded = isMenuExpanded(item.key);
	const isActive = isActiveRoute(item.route);
	const hasActiveChild = hasChildren && isChildActive(item.children ?? []);
			// Special handling for AI Candidates - show modal instead of navigating
			const handleClick = item.key === AI_CANDIDATES_MENU_KEY 
				? handleAICandidatesClick 
				: () => openItemOnSideBarClick(item, hasChildren, toggleMenu, navigate);
			
			return (
				<SidebarMenuItem
					key={item.key}
					item={item}
					level={level}
					isExpanded={isExpanded}
					isActive={isActive}
					hasActiveChild={hasActiveChild}
					onClick={handleClick}
					toggleMenu={toggleMenu}
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					renderChildren={(children, _unusedParentKey) => renderMenuItems(children || [], item.key, level + 1)}
				/>
			);
		});

	return (
		<>
			<aside
				className="sidebar-bg flex h-screen flex-col items-center border-r border-white/10 fixed left-0 top-0 z-20 text-white"
				style={{ width: `${SIDEBAR_EXPANDED_WIDTH_PX}px` }}>
				<SidebarLogo />
				<nav className="w-full flex-1 overflow-y-auto">
					<ul className="space-y-0 px-0 pt-0 w-full">{renderMenuItems(menu)}</ul>
				</nav>
			</aside>
			
			{/* AI Filter Modal - Shows when clicking AI Powered Candidates from sidebar */}
			<FilterModal
				isOpen={showAIFilterModal}
				onClose={() => setShowAIFilterModal(false)}
				config={aiFilterModalConfig}
				onApply={handleAIFilterApply}
				isLoading={!jobSkills || jobSkills.length === 0}
			/>
		</>
	);
};

export default Sidebar;
